import React, { useState, useRef, useEffect, useContext } from 'react';
import { useTheme, Theme, CSSObject } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import HomeIcon from '@mui/icons-material/Home';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import AssessmentIcon from '@mui/icons-material/Assessment';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import MenuIcon from '@mui/icons-material/Menu';
import HomeSettingsCard from '../../User/UserSettings/HomeSettingsCard';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import NotificationsIcon from '@mui/icons-material/Notifications';
import UserContext from '../../User/UserSettings/UserContext';
import { useStorageStore } from '../../Stores/StorageStore';
import NotificationWidget from '../../Home/NotificationWidget';
import {
    Tooltip, List, Divider, IconButton, ListItem,
    ListItemButton, ListItemText, ListItemIcon, Toolbar, Box, useMediaQuery,
    Badge, Popover
} from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import { AppBar, Drawer as MiniDrawer, DrawerHeader } from './DrawerHelpers';
import { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';

interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
}

interface MenuOption {
    text: string;
    icon: React.ReactNode;
    link: string;
}

function PersistentDrawer({ children }: any) {
    const { user } = useContext(UserContext) || { user: {} };
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [open, setOpen] = useState(false);
    const [openAccountCard, setOpenAccountCard] = useState(false);
    const cardRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const { notifications, loadNotifications, dismissNotification, setSelectedStorage, notificationsLoading } = useStorageStore();
    const [notifAnchorEl, setNotifAnchorEl] = useState<HTMLButtonElement | null>(null);

    const handleNotifClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setNotifAnchorEl(event.currentTarget);
    };

    const handleNotifClose = () => {
        setNotifAnchorEl(null);
    };

    useEffect(() => {
        if (user?.mobile && user?.id) {
             loadNotifications(user.id.toString());
        }
    }, [user, loadNotifications]);

    const openNotif = Boolean(notifAnchorEl);
    const idNotif = openNotif ? 'notification-popover' : undefined;

    const handleDrawerToggle = () => {
        setOpen(!open);
    };

    const handleDrawerClose = () => {
        setOpen(false);
    };

    const closeCard = () => {
        setOpenAccountCard(false);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            // Close the card if clicked outside it and not on the account icon button
            if (
                openAccountCard &&
                cardRef.current &&
                !cardRef.current.contains(event.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                closeCard();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openAccountCard]);
    const menuOptions: MenuOption[] = [
        { text: 'Home', icon: <HomeIcon />, link: '/' },
        { text: 'Analytics', icon: <AutoGraphIcon />, link: '/analytics' },
        { text: 'Reports', icon: <AssessmentIcon />, link: '/reports' },
        { text: 'Work History', icon: <WorkHistoryIcon />, link: '/work-history' },
        { text: 'Inspection Form', icon: <SearchIcon />, link: '/inspection-form' },
        // Only include Admin Controls if user is an Admin
        ...(user.accountLevel === 'Admin' ? [
            { text: 'Admin Controls', icon: <AdminPanelSettingsIcon />, link: '/admin' }
        ] : [])
    ]

    const drawerContent = (
        <>
            <DrawerHeader>
                <IconButton onClick={handleDrawerClose}>
                    {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                </IconButton>
            </DrawerHeader>
            <Divider />
            <List>
                {menuOptions.map((obj) => (
                    <Tooltip key={obj.text} title={!open && !isMobile ? obj.text : ""} placement="right">
                        <ListItem key={obj.text} disablePadding sx={{ display: 'block' }}>
                            <ListItemButton
                                onClick={() => {
                                    navigate(obj.link);
                                    if (isMobile) setOpen(false);
                                }}
                                sx={{
                                    minHeight: 48,
                                    px: 2.5,
                                    justifyContent: open ? 'initial' : 'center',
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        justifyContent: 'center',
                                        mr: open ? 3 : 'auto',
                                    }}
                                >
                                    {obj.icon}
                                </ListItemIcon>
                                <ListItemText
                                    primary={obj.text}
                                    sx={{ opacity: open ? 1 : 0 }}
                                />
                            </ListItemButton>
                        </ListItem>
                    </Tooltip>
                ))}
            </List>
        </>
    );

    return (
        <Box sx={{ display: 'flex' }}>
            <AppBar position="fixed" open={open && !isMobile}>
                <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Box>
                        <IconButton
                            onClick={handleDrawerToggle}
                            edge="start"
                            sx={[{
                                marginRight: 5
                            },
                            (open && !isMobile) && { display: 'none' },
                            ]}
                        >
                            <MenuIcon />
                        </IconButton>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                        {user?.mobile && (
                            <>
                                <IconButton color="inherit" onClick={handleNotifClick}>
                                    <Badge badgeContent={notifications.length} color="error">
                                        <NotificationsIcon />
                                    </Badge>
                                </IconButton>
                                <Popover
                                    id={idNotif}
                                    open={openNotif}
                                    anchorEl={notifAnchorEl}
                                    onClose={handleNotifClose}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'left',
                                    }}
                                >
                                    <Box sx={{ width: 320, maxHeight: 500 }}>
                                        <NotificationWidget
                                            notifications={notifications}
                                            onDismissNotification={dismissNotification}
                                            onStorageSelect={setSelectedStorage}
                                            loading={notificationsLoading}
                                        />
                                    </Box>
                                </Popover>
                            </>
                        )}
                        <IconButton
                            onClick={() => setOpenAccountCard(!openAccountCard)}
                            ref={buttonRef}
                        >
                            <AccountCircleIcon />
                        </IconButton>
                    </Box>
                    {openAccountCard && (
                        <Box
                            ref={cardRef}
                            sx={{
                                position: 'absolute',
                                top: '100%',
                                right: 10,
                                zIndex: 1300,
                                mt: 1
                            }}
                        >
                            <HomeSettingsCard closeCard={closeCard} />
                        </Box>
                    )}
                </Toolbar>
            </AppBar>
            {isMobile ? (
                <MuiDrawer
                    variant="temporary"
                    open={open}
                    onClose={handleDrawerClose}
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile.
                    }}
                    sx={{
                        display: { xs: 'block', md: 'none' }, 
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
                    }}
                >
                    {drawerContent}
                </MuiDrawer>
            ) : (
                <MiniDrawer variant="permanent" open={open}>
                    {drawerContent}
                </MiniDrawer>
            )}
            <Box component="main" sx={{
                flexGrow: 1,
                p: { xs: 1, sm: 3 },
                overflowX: 'hidden'
            }}>
                <DrawerHeader />
                {children}
            </Box>
        </Box>
    );
}

export default PersistentDrawer;